<template>
  <LcModal
    :close-triggers="['click-outside', 'close-button', 'escape']"
    :container-class="$style.modal"
    spacing="medium"
    :tracking="{
      action: 'play_high_frequency_game',
      topicKey: 'high-frequency-game-quickpick-modal',
    }"
    @close="emit('close')"
  >
    <template #header>
      <span class="inline-block w-full text-left headline-h4-headline">
        {{ $t('highfrequencygame.quickpickmodal.headline') }}
      </span>
    </template>

    <LcCard
      body-class="px-150 pb-150 pt-100"
      class="w-full bg-accent-info-info-extralight"
      header-class="px-150 pt-150"
      shadow="none"
    >
      <template #header>
        <div class="flex flex-col gap-050">
          <h2 class="headline-h5-headline text-left">
            {{ $t('highfrequencygame.quickpickmodal.selector.headline') }}
          </h2>
          <p class="paragraph-small-text text-secondary-secondary text-left">
            {{
              $t('highfrequencygame.quickpickmodal.selector.description', {
                min: minNumberOfSpots,
                max: maxNumberOfSpots,
              })
            }}
          </p>
        </div>
      </template>

      <HighFrequencyGameQuickPickSpotSelector
        v-model="selectedNumberOfSpots"
        :excluded-by-add-ons="restrictedAddOnNumbers"
        :max="maxNumberOfSpots"
        :min="minNumberOfSpots"
        :style="theme"
      />
    </LcCard>

    <LcInfoText
      v-if="addOnDisclaimer"
      class="w-full mt-100 text-secondary-secondary"
      icon-alignment="center"
      icon-size="small"
      size="small"
    >
      {{ addOnDisclaimer }}
    </LcInfoText>

    <LcButton class="w-full mt-200" :disabled="!selectedNumberOfSpots" @click="quickPick">
      {{ $t('highfrequencygame.quickpickmodal.cta.quickick') }}
    </LcButton>
  </LcModal>
</template>

<script lang="ts" setup>
type Emits = {
  (e: 'close'): void
}

const emit = defineEmits<Emits>()

const { config, gameId, setNumbers } = useHighFrequencyGame()
const { locale } = useI18n()

const selectedNumberOfSpots = ref<Maybe<number>>(null)

const numbersConfig = computed(() => config.value?.numbers[0])

const maxNumber = computed(() => numbersConfig.value?.max ?? 0)

const minNumber = computed(() => numbersConfig.value?.min ?? 0)

const maxNumberOfSpots = computed(() => numbersConfig.value?.selectableMax ?? 0)

const minNumberOfSpots = computed(() => numbersConfig.value?.selectableMin ?? 0)

const restrictedAddOn = computed(() =>
  config.value?.addOns.find((addOn) =>
    addOn.restrictions.some(isHighFrequencyAddOnExcludeSelectedNumbers),
  ),
)

const restrictedAddOnNumbers = computed(
  () =>
    restrictedAddOn.value?.restrictions.find(isHighFrequencyAddOnExcludeSelectedNumbers)
      ?.selected ?? [],
)

const addOnDisclaimer = computed(() => {
  if (!restrictedAddOn.value || !restrictedAddOnNumbers.value.length) return

  return $t('highfrequencygame.quickpickmodal.hint.restrictedaddon', {
    addOn: $t(`highfrequencygame.addon.${restrictedAddOn.value.name}`),
    spots: localizeListOfNumbers(restrictedAddOnNumbers.value, locale.value),
  })
})

const { createCssVar } = getLotteryCssVarConstructor(useLicensedTerritory(), gameId)

const theme = computed(() => ({
  '--number-color-background': createCssVar('high-frequency-ball-selected-background'),
  '--number-color-border': createCssVar('high-frequency-ball-selected-border-color'),
  '--number-color-text': createCssVar('high-frequency-ball-selected-text-color'),
}))

const quickPick = () => {
  if (!selectedNumberOfSpots.value || !numbersConfig.value) return

  setNumbers(
    numbersConfig.value.name,
    quickPickNumbers({
      max: maxNumber.value,
      min: minNumber.value,
      selectableMax: selectedNumberOfSpots.value,
    }),
    true,
  )

  emit('close')
}
</script>

<style lang="scss" module>
.modal {
  > * {
    max-width: 21.813rem;
  }
}
</style>
